import { FileWarning, Loader2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Document, Page, pdfjs } from "react-pdf";

import { api } from "@/lib/api";

// El worker se copia a /public en build/deploy. Servirlo como asset estático del
// mismo origen evita problemas de MIME y CORS que rompen `new URL(...)` en CRA.
// Usamos `.js` (no `.mjs`) porque algunos CDNs/proxies sirven `.mjs` como
// `application/octet-stream`, lo que rompe la carga del worker en producción.
pdfjs.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL || ""}/pdf.worker.min.js`;

export function isPdfFile(file) {
  return (
    (file?.content_type || "").includes("pdf") ||
    (file?.original_filename || "").toLowerCase().endsWith(".pdf")
  );
}

const Spinner = () => (
  <div className="h-full min-h-[200px] flex items-center justify-center" data-testid="file-preview-loading">
    <Loader2 className="h-6 w-6 animate-spin text-primary" strokeWidth={1.5} />
  </div>
);

/** Visor universal de archivos: PDFs renderizados con PDF.js (funciona también en
 *  celulares, donde los navegadores no muestran PDFs dentro de iframes) e imágenes
 *  con <img>. Descarga el archivo autenticado vía axios (cookies) como blob. */
export function FilePreview({ file, heightClass = "h-[480px]" }) {
  const containerRef = useRef(null);
  const [blobUrl, setBlobUrl] = useState(null);
  const [error, setError] = useState(false);
  const [numPages, setNumPages] = useState(0);
  const [width, setWidth] = useState(0);

  useEffect(() => {
    let url = null;
    let cancelled = false;
    setBlobUrl(null);
    setError(false);
    setNumPages(0);
    api
      .get(`/files/${file.file_id}`, { responseType: "blob" })
      .then((r) => {
        if (cancelled) return;
        url = URL.createObjectURL(r.data);
        setBlobUrl(url);
      })
      .catch(() => {
        if (!cancelled) setError(true);
      });
    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [file.file_id]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const measure = () => setWidth(el.clientWidth);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return (
    <div
      ref={containerRef}
      className={`${heightClass} overflow-auto border rounded-xl bg-secondary/30`}
      data-testid="file-preview"
    >
      {error && (
        <div
          className="h-full flex flex-col items-center justify-center gap-2 text-sm text-muted-foreground p-6 text-center"
          data-testid="file-preview-error"
        >
          <FileWarning className="h-6 w-6" strokeWidth={1.5} />
          No se pudo cargar la vista previa. Usa "Abrir en pestaña nueva".
        </div>
      )}
      {!error && !blobUrl && <Spinner />}
      {!error && blobUrl && isPdfFile(file) && (
        <Document
          file={blobUrl}
          onLoadSuccess={({ numPages: n }) => setNumPages(n)}
          onLoadError={() => setError(true)}
          loading={<Spinner />}
        >
          {Array.from({ length: numPages }, (_, i) => (
            <Page
              key={i}
              pageNumber={i + 1}
              width={Math.max(width - 2, 100)}
              renderTextLayer={false}
              renderAnnotationLayer={false}
              className="mb-2 bg-white"
            />
          ))}
        </Document>
      )}
      {!error && blobUrl && !isPdfFile(file) && (
        <div className="flex items-start justify-center min-h-full">
          <img src={blobUrl} alt={file.original_filename} className="max-w-full" data-testid="img-preview" />
        </div>
      )}
    </div>
  );
}
